/**
 * The editor half of the window.
 *
 * The behaviour that matters: every run is recorded and buffered. The learner hits
 * run far more often than they type, and each run costs nothing — the buffer rides
 * along with their next message. That is the pilot's paste ritual made invisible,
 * without turning a tinker loop into forty API calls.
 */

import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { python } from "@codemirror/lang-python";
import { useMemo } from "react";

import type { EditorRunPayload } from "../api";
import type { RunResult } from "../runners/types";
import type { SoundsCatalogue } from "../sounds";
import { SoundsBrowser } from "./SoundsBrowser";

interface Props {
  code: string;
  onCodeChange: (code: string) => void;
  language: string;
  runLabel: string;
  canStop: boolean;
  /** True while a runner is actually making sound. */
  playing: boolean;
  running: boolean;
  loadingMessage: string | null;
  lastResult: RunResult | null;
  buffered: EditorRunPayload[];
  stdinText: string;
  onStdinChange: (text: string) => void;
  needsStdin: boolean;
  onRun: () => void;
  onStop: () => void;
  /** What to call the live state in the header: "playing", or "running" for a sketch. */
  liveLabel: string;
  /** True when the runner draws and needs somewhere to draw into. */
  needsSurface: boolean;
  /** Handed to the runner as its drawing surface. */
  surfaceRef: (element: HTMLDivElement | null) => void;
  sounds: SoundsCatalogue | null;
  soundsLoading: boolean;
  soundsError: string | null;
  onPickSound: (name: string) => void;
  onCursorChange: (position: number) => void;
  /** The question a paused program is waiting on, or null when nothing is waiting. */
  asking: string | null;
  answerText: string;
  onAnswerChange: (text: string) => void;
  onAnswerSubmit: () => void;
}

const OUTCOME_LABEL: Record<string, string> = {
  ok: "ran",
  silent: "ran, no output",
  error: "error",
  played: "playing",
  drawing: "running",
};

export function EditorPanel({
  code,
  onCodeChange,
  language,
  runLabel,
  canStop,
  playing,
  running,
  loadingMessage,
  lastResult,
  buffered,
  stdinText,
  onStdinChange,
  needsStdin,
  onRun,
  onStop,
  liveLabel,
  needsSurface,
  surfaceRef,
  sounds,
  soundsLoading,
  soundsError,
  onPickSound,
  onCursorChange,
  asking,
  answerText,
  onAnswerChange,
  onAnswerSubmit,
}: Props) {
  const extensions = useMemo(
    () => [language === "python" ? python() : javascript()],
    [language],
  );

  // Memoised for its identity, not its cost. As an inline literal this is a new
  // object on every render, so react-codemirror reconfigures the editor, the
  // reconfiguration dispatches an update, `onUpdate` reports a caret position,
  // that sets state in App, and the render loop starts again — React eventually
  // gives up with "Maximum update depth exceeded".
  const basicSetup = useMemo(
    () => ({ lineNumbers: true, foldGutter: false, highlightActiveLine: true }),
    [],
  );

  return (
    <section className="panel editor" aria-label="Editor">
      <header className="panel-head">
        <div className="panel-title">Your code</div>
        <div className="actions">
          {/* Only live when there is something to stop. A permanently enabled Stop
              told the learner nothing, and read as "it is playing" when it was not. */}
          {canStop && (
            <button type="button" onClick={onStop} className="ghost" disabled={!playing}>
              Stop
            </button>
          )}
          <button type="button" onClick={onRun} disabled={running} className="primary">
            {running ? "Starting…" : playing ? `${runLabel} again` : runLabel}
          </button>
          {playing && (
            <span className="playing-dot" role="status" aria-label={liveLabel}>
              <span className="dot" />
              {liveLabel}
            </span>
          )}
        </div>
      </header>

      <CodeMirror
        value={code}
        height="100%"
        theme="dark"
        extensions={extensions}
        onChange={onCodeChange}
        onUpdate={(update) => {
          // Track the caret so a clicked sound lands where the learner is typing.
          // Only when it actually moved: onUpdate fires for every update the view
          // makes, including ones triggered by React re-rendering this component.
          if (update.selectionSet || update.docChanged) {
            onCursorChange(update.state.selection.main.head);
          }
        }}
        className="codemirror"
        basicSetup={basicSetup}
      />

      {needsSurface && (
        <div className="stage">
          {/* The runner owns everything inside this element. It is always mounted,
              not conditional on a run having happened: mounting it late would mean
              the runner has nowhere to draw at the moment the learner presses Run. */}
          <div className="stage-surface" ref={surfaceRef} aria-label="Your sketch" />
          <div className="stage-hint">
            Click the picture before using the keyboard — that is how the keys reach
            your sketch.
          </div>
        </div>
      )}

      {(sounds || soundsLoading || soundsError) && (
        <SoundsBrowser
          catalogue={sounds}
          loading={soundsLoading}
          error={soundsError}
          onPick={onPickSound}
        />
      )}

      {asking !== null && (
        <div className="asking">
          <div className="asking-head">
            <span className="dot" />
            Your program is waiting for you
          </div>
          {/* The prompt is whatever the program printed before it asked — usually the
              question itself, e.g. input("What is your name? "). Shown here as well as
              in the output so the learner does not have to look in two places. */}
          {asking.trim() && <pre className="asking-prompt">{asking.replace(/\n+$/, "")}</pre>}
          <div className="asking-row">
            <input
              autoFocus
              value={answerText}
              onChange={(event) => onAnswerChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  onAnswerSubmit();
                }
              }}
              placeholder="type your answer and press Enter"
              aria-label="Answer for your program"
              spellCheck={false}
            />
            <button type="button" className="primary" onClick={onAnswerSubmit}>
              Enter
            </button>
          </div>
        </div>
      )}

      {needsStdin && (
        <div className="stdin">
          <label htmlFor="stdin">
            If your program uses <code>input()</code>, put the answers here — one per line
          </label>
          <textarea
            id="stdin"
            value={stdinText}
            onChange={(event) => onStdinChange(event.target.value)}
            onKeyDown={(event) => {
              // Enter runs; Shift+Enter adds another answer. Without a commit gesture
              // the box just sits there and it is not obvious that Run is what consumes
              // it — a learner types an answer, nothing happens, and they type it again.
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                if (!running) onRun();
              }
            }}
            rows={2}
            spellCheck={false}
            placeholder={"Sparks\n12"}
          />
          <div className="stdin-hint">
            Enter to {runLabel.toLowerCase()} · Shift+Enter for another answer · the box
            empties each run
          </div>
        </div>
      )}

      <div className="output" aria-live="polite">
        {loadingMessage && <div className="muted">{loadingMessage}</div>}
        {!loadingMessage && !lastResult && (
          <div className="muted">Press {runLabel} to see what it does.</div>
        )}
        {lastResult && (
          <>
            <div className={`outcome outcome-${lastResult.outcome}`}>
              {OUTCOME_LABEL[lastResult.outcome] ?? lastResult.outcome}
            </div>
            <pre>{lastResult.detail}</pre>
          </>
        )}
      </div>

      <footer className="panel-foot">
        {buffered.length === 0 ? (
          <span className="muted">Nothing to show the tutor yet.</span>
        ) : (
          <span>
            {buffered.length} run{buffered.length === 1 ? "" : "s"} will go with your next
            message.
          </span>
        )}
      </footer>
    </section>
  );
}
